import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import ServiceCard from "../../components/ServiceCard";
import { formatCategories } from "../../utils/categoryFormatter";

const FreelancerServicesListing = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [professional, setProfessional] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [publicNotice, setPublicNotice] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchFreelancerData();
  }, [slug]);

  const fetchFreelancerData = async () => {
    try {
      setLoading(true);
      setPublicNotice(null);
      setError("");

      // Fetch freelancer data by slug
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/staff/slug/${slug}`;
      const response = await fetch(url);

      if (!response.ok) {
        let errorPayload = null;
        try {
          errorPayload = await response.json();
        } catch (_err) {}

        if (
          errorPayload?.registrationInProgress ||
          errorPayload?.error === "registration_in_progress"
        ) {
          setPublicNotice({
            title: errorPayload?.title || "Professional setup in progress",
            message:
              errorPayload?.message ||
              "Your favorite professional is still registering. Please check back in a few hours.",
            ownerMessage: errorPayload?.ownerMessage || "",
            loginPath: errorPayload?.loginPath || "/login",
          });
          return;
        }

        throw new Error(errorPayload?.message || "Freelancer not found");
      }

      const data = await response.json();
      setProfessional(data);
      setServices(data.services || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceBook = (service) => {
    navigate(
      `/freelancer-booking?staffId=${professional._id}&serviceId=${service._id}`,
    );
  };

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ fontSize: "18px", color: "#6b7280" }}>
            Loading services...
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const hasSession = Boolean(localStorage.getItem("ogestyle_token"));

  if (publicNotice) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
            maxWidth: "760px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              background: "#f8fbff",
              border: "1px solid #bfdbfe",
              borderRadius: "12px",
              padding: "24px",
            }}
          >
            <div
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#1e3a8a",
                marginBottom: "10px",
              }}
            >
              {publicNotice.title}
            </div>
            <div
              style={{
                fontSize: "16px",
                color: "#334155",
                marginBottom: "10px",
              }}
            >
              {publicNotice.message}
            </div>
            {publicNotice.ownerMessage && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#0f766e",
                  marginBottom: "18px",
                }}
              >
                {publicNotice.ownerMessage}
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              {!hasSession && (
                <button
                  onClick={() => navigate(publicNotice.loginPath || "/login")}
                  style={{
                    padding: "10px 18px",
                    background: "#1d4ed8",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Log In to Finish Registration
                </button>
              )}
              <button
                onClick={() => navigate("/")}
                style={{
                  padding: "10px 18px",
                  background: "#e2e8f0",
                  color: "#0f172a",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error) {
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            padding: "60px 20px",
            textAlign: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ fontSize: "18px", color: "#ef4444" }}>{error}</div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div
        style={{
          paddingTop: "80px",
          minHeight: "100vh",
          background: "linear-gradient(to bottom, #f9fafb 0%, #ffffff 300px)",
        }}
      >
        {/* Header Section */}
        <div
          style={{
            background: "white",
            borderBottom: "1px solid #e5e7eb",
            padding: isMobile ? "24px 16px" : "40px 48px",
          }}
        >
          <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
            {/* Back Button */}
            <button
              onClick={() => navigate(`/freelancer/${slug}`)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "none",
                border: "none",
                color: "#1a659e",
                fontWeight: "600",
                fontSize: "14px",
                cursor: "pointer",
                marginBottom: "24px",
                padding: "8px 0",
              }}
            >
              ← Back to Profile
            </button>

            {/* Professional Info */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                flexWrap: "wrap",
              }}
            >
              {professional.profileImage && (
                <img
                  src={professional.profileImage}
                  alt={
                    professional.businessName ||
                    `${professional.firstName} ${professional.lastName}`
                  }
                  style={{
                    width: isMobile ? "80px" : "100px",
                    height: isMobile ? "80px" : "100px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "3px solid #1a659e",
                  }}
                />
              )}
              <div>
                <h1
                  style={{
                    fontSize: isMobile ? "24px" : "32px",
                    fontWeight: "700",
                    color: "#1a1a1a",
                    margin: "0 0 8px 0",
                  }}
                >
                  {professional.businessName ||
                    `${professional.firstName} ${professional.lastName}`}
                </h1>
                <p
                  style={{
                    fontSize: "16px",
                    color: "#6b7280",
                    margin: 0,
                  }}
                >
                  Browse all services and book your appointment
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div
          style={{
            padding: isMobile ? "32px 16px" : "48px 48px",
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          {services.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                color: "#6b7280",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
              <h3 style={{ fontSize: "20px", marginBottom: "8px" }}>
                No Services Available
              </h3>
              <p>This professional hasn't added any services yet.</p>
            </div>
          ) : (
            <ul className="service-tiles">
              {services.map((service) => (
                <li key={service._id}>
                  <ServiceCard
                    service={service}
                    businessType="freelancer"
                    onSelect={() => handleServiceBook(service)}
                    ctaLabel="Book Now"
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default FreelancerServicesListing;